import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { BehaviorSubject, catchError, filter, finalize, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private apiRoot: string = environment.apiUrl + 'Accounts/';
    public otpTrue: boolean;
    private isRefreshing = false; // Stato di aggiornamento del token
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private refreshInterval:NodeJS.Timeout;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }
    set user(usr: string) {
        console.log("user in authService");
        console.log(JSON.parse(localStorage.getItem('user')));

        localStorage.setItem('user', JSON.stringify(usr));
    }

    get user(): string {
        console.log(JSON.parse(localStorage.getItem('user')));
        return JSON.parse(localStorage.getItem('user')) ?? null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        const request = {
            Username: email,
            Email: email
        };
        return this._httpClient.post(`${this.apiRoot}ResetPassword`, request);
    }


    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post(`${this.apiRoot}api/auth/reset-password`, { password });
    }

    //Password
    changePassword(password: any): Observable<any> {
        return this._httpClient.post<any>(`${this.apiRoot}ChangePassword`, password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { username: string; password: string; extraData?: any }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        const payload = {
            username: credentials.username,
            password: credentials.password,
            extraData: credentials.extraData || {}
        };

        return this._httpClient.post(`${this.apiRoot}Login`, payload).pipe(
            switchMap((response: any) => {
                if (response.isAuthSuccessful && response.token) {
                    // Store the access token in the local storage
                    this.accessToken = response.token;
                    this.user = response.user;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    this.refreshInterval = setInterval(() => {
                        this.updateToken();
                    }, (1000 * 60 * 60)); //1H

                    // Return a new observable with the response
                    return of(response);
                } else {
                    return throwError('Authentication failed');
                }
            }),
            catchError(error => {
                console.error('Login failed:', error);
                return throwError(error);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {

        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;
        this.otpTrue = false;
        localStorage.setItem('otpTrue', 'false');
        localStorage.clear();

        clearInterval(this.refreshInterval);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(data: any): Observable<any> {
        /*return this._httpClient.post(`${this.apiRoot}Register`, data).pipe(
            switchMap((registerResponse: any) => {
                // Log dei dati prima di passare a signIn
                console.log('Register Response:', registerResponse);
                console.log('Dati di registrazione:', data);
                console.log('Dati interni:', data.data);

                // Dopo la registrazione, effettua il login
                const username = data.data.Email;
                const password = data.data.Password;
                return this.signIn({ username, password });
            }),
            catchError(error => {
                console.error('Registration or Login failed:', error);
                return throwError(error);
            })
        );*/
        return this._httpClient.post(`${this.apiRoot}Register`, data);
    }




    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post(`${this.apiRoot}api/auth/unlock-session`, credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {

        this.otpTrue = JSON.parse(localStorage.getItem('otpTrue'));
        
        if(!environment.enableTwoFactor){

            // Check if the user is logged in
            if (this._authenticated && !AuthUtils.isTokenAnonymous(this.accessToken)) {
                return of(true);
            }

            // Check the access token availability
            if (!this.accessToken) {
                return of(false);
            }

            // Check the access token expire date
            if (AuthUtils.isTokenExpired(this.accessToken)) {
                return of(false);
            }


            if (AuthUtils.isTokenAnonymous(this.accessToken))
                return of(false);
        }
        else{
            if (this._authenticated && !AuthUtils.isTokenAnonymous(this.accessToken) && this.otpTrue) {
                return of(true);
            }   
            
            // Check the access token availability
            if (!this.accessToken) {
                return of(false);
            }

            // Check the access token expire date
            if (AuthUtils.isTokenExpired(this.accessToken)) {
                return of(false);
            }


            if (AuthUtils.isTokenAnonymous(this.accessToken))
                return of(false);

            if(!this.otpTrue)
                return of(false);
        }  

        return of(true);
        }

    verifyTotp(data: any): Observable<any> {
        return this._httpClient.post(`${environment.apiUrl}api/Auth/verify-otp`, data);
    }

    registerTotp(data: any): Observable<any> {
        return this._httpClient.post(`${environment.apiUrl}api/Auth/register-totp`, data);
    }

    factorSidRegister(data: any): Observable<any> {
        return this._httpClient.post(`${this.apiRoot}FactorSidRegistration`, data);
    }

    /*approveUser(userSid: string, approve: boolean): Observable<any> {
        return this._httpClient.post(`${this.apiRoot}ApproveUser`, { id: userSid, approve: approve }, {
            headers: { 'Content-Type': 'application/json' }
        });
    }*/

    approveUser(userSid: string, approve: boolean, codiceBP?: string): Observable<any> {
        // Creiamo un oggetto per il corpo della richiesta
        const body: any = {
            id: userSid,
            approve: approve
        };

        // Aggiungi codiceBP solo se approve è true e codiceBP è definito
        if (approve && codiceBP) {
            body.codiceBP = codiceBP;
        }

        return this._httpClient.post(`${this.apiRoot}ApproveUser`, body, {
            headers: { 'Content-Type': 'application/json' }
        });
    }


    confirmEmail(userSid: string): Observable<any> {
        const makeRequest = (token: string) => {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            return this._httpClient.post(
                `${this.apiRoot}ConfirmEmail`,
                { id: userSid, codiceBP: '' },
                { headers }
            );
        };
    
        if (this.accessToken) {
            return makeRequest(this.accessToken);
        } else {
            return this.getAnonToken().pipe(
                switchMap((response: any) => {
                    const anonToken = response.token || response; // supporta sia stringa che oggetto
                    return makeRequest(anonToken);
                })
            );
        }
    }

    getAnonToken(): Observable<any> {
        return this._httpClient.get(`${this.apiRoot}anonymous-token`);
    }

    updateToken(): Observable<any> {

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            return throwError(() => new Error('Access token not found'));
        }
    
        if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => of(token)) 
            );
        }
    
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);
    
        return this._httpClient.get(`${this.apiRoot}refresh-token`).pipe(
            tap(() => console.log('Invio della richiesta al server...')),
            map((response: any) => {
                console.log('Risposta ricevuta:', response);
                const newToken = response.accessToken;
                if (newToken) {
                    localStorage.setItem('accessToken', newToken);
                    this.accessToken = newToken;
                    this.refreshTokenSubject.next(newToken); 
                }
                return newToken;
            }),
            catchError(error => {
                console.error('Errore durante il refresh del token:', error);
                this.signOut();
                this.refreshTokenSubject.next(null); 
                return throwError(() => error);
            }),
            finalize(() => {
                this.isRefreshing = false;
            })
        );
    }
    
    
    
    

}
